<template>
    <div v-if="vehicleAddons.length > 0" class="vehicle-addons vehicle-section">
        <h3 class="vehicle-section__heading">
            {{ title }}
        </h3>

        <ul v-if="vehicleAddons.length > 0" class="vehicle-addons__list">
            <li v-for="addon in slicedVehicleAddons" :key="addon.key" class="vehicle-addons__list__item">
                <div class="vehicle-addons__list__addon-title">
                    <div class="vehicle-addons__list__wrapper">
                        <svg-icon :name="iconName(addon.key)" />
                        <strong v-if="isBikeRackAddon(addon.key)">{{
                            $t('pages.vehicle.equipment_addon.bike_rack')
                        }}</strong>
                        <strong v-else>{{ $t(`pages.vehicle.${type}.${addon.key}`) }}</strong>
                    </div>
                    <span v-if="isBikeRackAddon(addon.key)">{{ `(${bikeRackCapacity(addon.key)})` }}</span>
                </div>

                <span v-if="isAddonIncluded(addon)">
                    {{ $t('pages.vehicle.addon_included_in_price') }}
                </span>
                <template v-else>
                    <div
                        v-if="!isLargeScreen"
                        class="vehicle-addons__list__wrapper vehicle-addons__list__price-wrapper"
                    >
                        <span class="vehicle-addons__list__price-type">
                            {{
                                addon.pricingStrategy === PRICING_STRATEGY_PER_BOOKING_NIGHT
                                    ? $t(`pages.vehicle.price_per_night`)
                                    : $t(`pages.vehicle.price_per_booking`)
                            }}
                        </span>
                        <span class="vehicle-addons__list__price-value">{{
                            `+ ${formattedPrice(addon.price, addon.currency, 2)}`
                        }}</span>
                    </div>
                    <span v-else>{{ `+ ${priceText(addon)}` }}</span>
                </template>
            </li>
        </ul>

        <button
            v-if="isShowAllVisible"
            class="vehicle-section__button button button--secondary-contrast button--small"
            @click="$emit('click')"
        >
            {{ btnText }}
        </button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import assetAddonsMixin from '~/mixins/assetAddonsMixin';
import { PRICING_STRATEGY_PER_BOOKING_NIGHT, TYPE_EQUIPMENT_ADDON } from '~/config/productTypes';

export default {
    name: 'VehicleAddons',
    mixins: [assetAddonsMixin],
    props: {
        addons: {
            type: Array,
            default: null,
        },
        type: {
            type: String,
            required: true,
        },
        displayAll: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            PRICING_STRATEGY_PER_BOOKING_NIGHT,
            sliceTo: 3,
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen']),
        vehicleAddons() {
            return this.type === TYPE_EQUIPMENT_ADDON
                ? this.equipmentAddons(this.addons)
                : this.serviceAddons(this.addons);
        },
        slicedVehicleAddons() {
            if (this.vehicleAddons.length > this.sliceTo && !this.displayAll) {
                return this.vehicleAddons.slice(0, this.sliceTo);
            }

            return this.vehicleAddons;
        },
        isShowAllVisible() {
            return this.vehicleAddons.length > this.sliceTo && !this.displayAll;
        },
        title() {
            return this.type === TYPE_EQUIPMENT_ADDON
                ? this.$t('pages.vehicle.equipment_addons_heading')
                : this.$t('pages.vehicle.service_addons_heading');
        },
        btnText() {
            return this.type === TYPE_EQUIPMENT_ADDON
                ? this.$t('pages.vehicle.show_all_equipment_addons')
                : this.$t('pages.vehicle.show_all_service_addons');
        },
    },
    methods: {
        iconName(key) {
            return this.type === TYPE_EQUIPMENT_ADDON ? this.equipmentIconName(key) : this.serviceIconName(key);
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.vehicle-addons {
    @include font-size(16px);

    &__list {
        .svg-icon {
            width: 24px;
            height: 24px;
            min-width: 24px;
            margin-right: 8px;
        }

        &__item {
            &:not(:last-child) {
                margin-bottom: 16px;
            }

            @include lg {
                columns: 2;
                column-gap: 20px;
            }
        }

        &__addon-title {
            padding-right: 16px;

            @include xl {
                display: flex;
                align-items: center;
            }
        }

        &__wrapper {
            display: flex;
            align-items: center;
            white-space: nowrap;

            @include xl {
                margin-right: 4px; // margin because of brackets after name of addon
            }
        }

        &__price-wrapper {
            justify-content: space-between;
            margin-top: 8px;
        }

        &__price-type {
            padding-right: 16px;
        }

        &__price-value {
            white-space: nowrap;
        }
    }

    .vehicle-section__button {
        margin-top: 32px;
    }
}
</style>
